// super-excalidraw: the "Animate" dialog. Builds a per-element timeline for the
// current saved diagram, renders it to video server-side (Go + Node worker),
// and plays the result inline with a download link. Themed entirely with
// Excalidraw's CSS custom properties so it tracks light/dark for free. Opened
// from the hamburger menu (a modal — no overlap with the toolbar).
//
// Render jobs are persisted to localStorage (keyed per diagram) so closing the
// dialog mid-render is safe — the backend keeps rendering, and the dialog
// resumes polling when reopened.

import React, { useEffect, useMemo, useRef, useState } from "react";

import { Dialog } from "@excalidraw/excalidraw/components/Dialog";

import type { ExcalidrawImperativeAPI } from "@excalidraw/excalidraw/types";

import {
  renderAnimation,
  waitForAnimation,
  getAnimSpec,
  putAnimSpec,
  animResultUrl,
  deleteAnimation,
  ANIM_TYPES,
  type AnimFormat,
  type AnimSpec,
  type AnimStatus,
  type AnimType,
} from "../data/animation";

import "./AnimationDialog.scss";

type Props = {
  diagramId?: string;
  excalidrawAPI: ExcalidrawImperativeAPI | null;
  onClose: () => void;
};

// "" in a per-row type select = "use the global default".
type Win = { start: number; duration: number; type?: AnimType };

// --- Render job persistence (localStorage, per diagram) ---

interface RenderRecord {
  jobId: string;
  format: AnimFormat;
  resolution: string;
  startedAt: number;
  finishedAt?: number;
  status: "queued" | "rendering" | "done" | "error";
  error?: string;
  spec?: AnimSpec; // snapshot stored so "Re-render" can restore the exact config
}

const LS_KEY = (id: string) => `se-jobs-${id}`;
const MAX_JOBS = 10;

function loadJobs(id: string): RenderRecord[] {
  try {
    return JSON.parse(localStorage.getItem(LS_KEY(id)) ?? "[]");
  } catch {
    return [];
  }
}

function upsertJob(
  id: string,
  patch: Partial<RenderRecord> & Pick<RenderRecord, "jobId">,
): RenderRecord[] {
  const jobs = loadJobs(id);
  const idx = jobs.findIndex((j) => j.jobId === patch.jobId);
  if (idx >= 0) {
    jobs[idx] = { ...jobs[idx], ...patch };
  } else {
    jobs.unshift(patch as RenderRecord);
  }
  const trimmed = jobs.slice(0, MAX_JOBS);
  try {
    localStorage.setItem(LS_KEY(id), JSON.stringify(trimmed));
  } catch {}
  return trimmed;
}

function removeJob(id: string, jobId: string): RenderRecord[] {
  const jobs = loadJobs(id).filter((j) => j.jobId !== jobId);
  try {
    localStorage.setItem(LS_KEY(id), JSON.stringify(jobs));
  } catch {}
  return jobs;
}

// --- Timeline defaults ---

const DEFAULT_DUR = 700;
const STEP = 750;
const WHOLE = "";

const RES_OPTIONS: { value: string; label: string }[] = [
  { value: "720", label: "720p" },
  { value: "1080", label: "1080p" },
  { value: "2k", label: "2K" },
  { value: "4k", label: "4K Ultra HD" },
];

// Per-element entrance styles for presentation (Magic-Move) mode. These map to
// customData.enter, read by both the live PresentationPlayer and the render
// worker. "" = no entrance (the element is simply present).
const PRESENT_ENTER: { value: string; label: string }[] = [
  { value: "", label: "None" },
  { value: "fade", label: "Fade in" },
  { value: "zoom", label: "Zoom / Pop" },
  { value: "rise", label: "Rise up" },
  { value: "typewriter", label: "Typewriter" },
  { value: "wipe", label: "Wipe (L→R)" },
  { value: "draw", label: "Draw-on" },
  { value: "appear", label: "Appear" },
];

// Continuous motion during the show (customData.loop). spin only applies to
// elements that stay on a single slide (a morphing element can't also spin).
const PRESENT_LOOP: { value: string; label: string }[] = [
  { value: "", label: "None" },
  { value: "float", label: "Float" },
  { value: "spin", label: "Spin" },
];

const elLabel = (e: any): string => {
  if (e.type === "text") {
    const t = String(e.text || "").replace(/\s+/g, " ").trim();
    return t ? `Text: ${t.slice(0, 28)}` : "Text";
  }
  if (e.type === "arrow" || e.type === "line") {
    return e.type === "arrow" ? "Arrow" : "Line";
  }
  return e.type.charAt(0).toUpperCase() + e.type.slice(1);
};

function relTime(ms: number): string {
  const s = Math.floor((Date.now() - ms) / 1000);
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  return `${Math.floor(s / 3600)}h ago`;
}

// --- Component ---

export const AnimationDialog: React.FC<Props> = ({ diagramId, excalidrawAPI, onClose }) => {
  const sceneEls = useMemo<any[]>(
    () => (excalidrawAPI ? (excalidrawAPI.getSceneElements() as any[]) : []),
    [excalidrawAPI],
  );
  const frames = useMemo(
    () => sceneEls.filter((e) => e.type === "frame" && !e.isDeleted),
    [sceneEls],
  );
  const allRenderable = useMemo(
    () => sceneEls.filter((e) => !e.isDeleted && e.type !== "frame"),
    [sceneEls],
  );

  const [frameId, setFrameId] = useState<string>(WHOLE);
  const [format, setFormat] = useState<AnimFormat>("mp4");
  const [resolution, setResolution] = useState<string>("1080");
  const [defaultType, setDefaultType] = useState<AnimType>("draw");
  const [windows, setWindows] = useState<Record<string, Win>>({});
  // Magic-Move presentation mode: render the frames as a morphing slide deck
  // (each element's customData.morphId/enter/loop drives it) instead of the
  // per-element entrance timeline. Only meaningful with 2+ frames.
  const [presentationMode, setPresentationMode] = useState(false);
  const [holdMs, setHoldMs] = useState(2200);
  const [morphMs, setMorphMs] = useState(850);
  // "morph" = Magic-Move (elements morph in place); "pan" = camera travels
  // frame-to-frame so inter-frame connector arrows are visible in the video.
  const [presentMode, setPresentMode] = useState<"morph" | "pan">("morph");
  // Local overrides for per-element customData.enter/loop/morphId, written
  // through to the live scene so the saved diagram (and the server render) pick
  // them up. Display merges these over each element's stored customData.
  const [elAnim, setElAnim] = useState<
    Record<string, { enter?: string; loop?: string; morphId?: string }>
  >({});
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState<AnimStatus | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [jobs, setJobs] = useState<RenderRecord[]>(() =>
    diagramId ? loadJobs(diagramId) : [],
  );
  // Inline player: which finished render is currently shown (plays in-dialog
  // instead of opening a new tab, which the PWA service worker would hijack
  // into the app shell).
  const [preview, setPreview] = useState<{ format: AnimFormat; url: string } | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const abortRef = useRef<AbortController | null>(null);

  const shown = useMemo(
    () => (frameId ? allRenderable.filter((e) => e.frameId === frameId) : allRenderable),
    [allRenderable, frameId],
  );

  const sequential = (els: any[]): Record<string, Win> => {
    const w: Record<string, Win> = {};
    els.forEach((e, i) => {
      w[e.id] = { start: i * STEP, duration: DEFAULT_DUR };
    });
    return w;
  };

  // On open: load saved spec from server (restores last render's timeline config).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      let next = sequential(allRenderable);
      if (diagramId) {
        try {
          const spec = await getAnimSpec(diagramId);
          if (spec?.frameId) setFrameId(spec.frameId);
          if (spec?.defaultType) setDefaultType(spec.defaultType);
          if (spec?.elements?.length) {
            for (const w of spec.elements) {
              next[w.id] = {
                start: w.start ?? 0,
                duration: Math.max(50, w.duration ?? DEFAULT_DUR),
                type: w.type,
              };
            }
          }
        } catch {
          // no saved spec — sequential default
        }
      }
      if (!cancelled) setWindows(next);
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [diagramId]);

  // On open: resume polling for any job that was in-flight when dialog was last closed.
  useEffect(() => {
    if (!diagramId) return;
    const pending = loadJobs(diagramId).find(
      (j) => j.status === "queued" || j.status === "rendering",
    );
    if (!pending) return;

    const ctrl = new AbortController();
    abortRef.current = ctrl;
    setBusy(true);
    setStatus({ status: pending.status });

    (async () => {
      try {
        const final = await waitForAnimation(
          diagramId,
          pending.jobId,
          (s) => {
            setStatus(s);
            setJobs(upsertJob(diagramId, { jobId: pending.jobId, status: s.status }));
          },
          8 * 60 * 1000,
          ctrl.signal,
        );
        if (final === null) return; // aborted (dialog closed) — backend render continues

        const patch: Partial<RenderRecord> & Pick<RenderRecord, "jobId"> = {
          jobId: pending.jobId,
          status: final.status,
          finishedAt: Date.now(),
          ...(final.status === "error" ? { error: final.error ?? "render failed" } : {}),
        };
        setJobs(upsertJob(diagramId, patch));
        setStatus(final);
        if (final.status === "error") setError(final.error ?? "render failed");
      } catch {
        // ignore — abort or network
      } finally {
        if (!ctrl.signal.aborted) {
          setBusy(false);
          abortRef.current = null;
        }
      }
    })();

    return () => {
      ctrl.abort();
      abortRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [diagramId]); // intentionally runs once per diagramId (= dialog open)

  // Abort polling on unmount so the loop doesn't run against an unmounted component.
  useEffect(() => {
    return () => {
      abortRef.current?.abort();
    };
  }, []);

  const setWin = (id: string, patch: Partial<Win>) =>
    setWindows((w) => {
      const cur: Win = w[id] ?? { start: 0, duration: DEFAULT_DUR };
      return { ...w, [id]: { ...cur, ...patch } };
    });

  const onResetSequential = () => setWindows(sequential(shown));

  // Restore format / resolution / timeline from a past job record.
  const onRerender = (job: RenderRecord) => {
    setFormat(job.format);
    setResolution(job.resolution);
    if (job.spec?.frameId) setFrameId(job.spec.frameId ?? WHOLE);
    if (job.spec?.defaultType) setDefaultType(job.spec.defaultType);
    if (job.spec?.elements?.length) {
      const next: Record<string, Win> = {};
      for (const w of job.spec.elements) {
        next[w.id] = { start: w.start ?? 0, duration: w.duration ?? DEFAULT_DUR, type: w.type };
      }
      setWindows(next);
    }
  };

  // Delete a finished render: remove the stored video from the server and drop
  // the record from the list. Clears the inline player if it was showing it.
  const onDeleteRender = async (job: RenderRecord) => {
    if (!diagramId || deletingId) return;
    if (!confirm(`Delete this ${job.format.toUpperCase()} render? This cannot be undone.`)) {
      return;
    }
    setDeletingId(job.jobId);
    try {
      if (job.status === "done") {
        await deleteAnimation(diagramId, job.format);
      }
      setJobs(removeJob(diagramId, job.jobId));
      setPreview((p) => (p && p.format === job.format ? null : p));
    } catch (e: any) {
      setError(e?.message ?? String(e));
    } finally {
      setDeletingId(null);
    }
  };

  // Slide order for presentation mode: saved presentationOrder, else frames
  // top-to-bottom (matches the server default).
  const slideOrder = useMemo(() => {
    const order = (excalidrawAPI?.getAppState() as any)?.customData
      ?.presentationOrder as string[] | undefined;
    const live = new Set(frames.map((f) => f.id));
    if (order?.length) return order.filter((id) => live.has(id));
    return frames.slice().sort((a, b) => a.y - b.y).map((f) => f.id);
  }, [excalidrawAPI, frames]);

  // Slide elements for the per-element animation pickers (presentation mode).
  // Only content that lives inside a slide frame — connectors (frameId null)
  // are excluded.
  const presentEls = useMemo(() => {
    const set = new Set(slideOrder);
    return allRenderable.filter((e) => e.frameId && set.has(e.frameId));
  }, [allRenderable, slideOrder]);

  // Write a per-element customData patch (enter / loop / morphId) through to the
  // live scene so it autosaves and the server render reads it. Empty string
  // clears the key. Bumps version so Excalidraw's autosave fires.
  const setElCustom = (
    id: string,
    patch: { enter?: string; loop?: string; morphId?: string },
  ) => {
    setElAnim((prev) => ({ ...prev, [id]: { ...prev[id], ...patch } }));
    if (!excalidrawAPI) return;
    const next = (excalidrawAPI.getSceneElements() as any[]).map((el) => {
      if (el.id !== id) return el;
      const cd = { ...(el.customData || {}) };
      for (const k of Object.keys(patch)) {
        const v = (patch as any)[k];
        if (v) cd[k] = v;
        else delete cd[k];
      }
      return {
        ...el,
        customData: cd,
        version: (el.version || 1) + 1,
        versionNonce: Math.floor(Math.random() * 2147483647),
      };
    });
    excalidrawAPI.updateScene({ elements: next as any });
  };

  // Display value for a picker: local override wins, else the element's stored
  // customData, else empty.
  const animVal = (e: any, key: "enter" | "loop" | "morphId"): string =>
    (elAnim[e.id]?.[key] ?? e.customData?.[key] ?? "") as string;

  const onRender = async () => {
    if (!diagramId || busy) return;

    const spec: AnimSpec = presentationMode
      ? { presentation: { frames: slideOrder, holdMs, morphMs, mode: presentMode } }
      : {
          frameId: frameId || undefined,
          defaultType,
          elements: shown.map((e) => ({
            id: e.id,
            start: windows[e.id]?.start ?? 0,
            duration: windows[e.id]?.duration ?? DEFAULT_DUR,
            type: windows[e.id]?.type, // undefined => use defaultType
          })),
        };

    // Persist the spec so the next dialog open restores these exact settings.
    putAnimSpec(diagramId, spec).catch(() => {});

    setBusy(true);
    setError(null);
    setStatus({ status: "queued" });

    const ctrl = new AbortController();
    abortRef.current = ctrl;

    let jobId: string;
    try {
      const res = await renderAnimation(diagramId, { spec, format, resolution });
      jobId = res.jobId;
    } catch (e: any) {
      setError(e?.message ?? String(e));
      setBusy(false);
      abortRef.current = null;
      return;
    }

    // Persist the job immediately — if the dialog closes before polling finishes,
    // the resume effect will pick it back up on next open.
    setJobs(
      upsertJob(diagramId, {
        jobId,
        format,
        resolution,
        startedAt: Date.now(),
        status: "queued",
        spec,
      }),
    );

    try {
      const final = await waitForAnimation(
        diagramId,
        jobId,
        (s) => {
          setStatus(s);
          setJobs(upsertJob(diagramId, { jobId, status: s.status }));
        },
        8 * 60 * 1000,
        ctrl.signal,
      );

      if (final === null) return; // aborted (dialog closed) — job persisted, resumes on reopen

      const patch: Partial<RenderRecord> & Pick<RenderRecord, "jobId"> = {
        jobId,
        status: final.status,
        finishedAt: Date.now(),
        ...(final.status === "error" ? { error: final.error ?? "render failed" } : {}),
      };
      setJobs(upsertJob(diagramId, patch));
      setStatus(final);
      if (final.status === "error") setError(final.error ?? "render failed");
    } catch (e: any) {
      if (!ctrl.signal.aborted) setError(e?.message ?? String(e));
    } finally {
      if (!ctrl.signal.aborted) {
        setBusy(false);
        abortRef.current = null;
      }
    }
  };

  return (
    <Dialog onCloseRequest={onClose} title="Animate diagram" size="wide">
      <div className="anim">
        {!diagramId ? (
          <div className="anim__notice">
            Save this diagram to the server first — server-side rendering needs a
            saved copy. Use "Save to server", then reopen Animate.
          </div>
        ) : (
          <>
            <div className="anim__bar">
              <label className="anim__ctl">
                <span>Frame</span>
                <select value={frameId} onChange={(e) => setFrameId(e.target.value)}>
                  <option value={WHOLE}>Whole scene</option>
                  {frames.map((f) => (
                    <option key={f.id} value={f.id}>
                      {f.name || "Frame"} ({Math.round(f.width)}×{Math.round(f.height)})
                    </option>
                  ))}
                </select>
              </label>
              <label className="anim__ctl">
                <span>Format</span>
                <select value={format} onChange={(e) => setFormat(e.target.value as AnimFormat)}>
                  <option value="mp4">MP4</option>
                  <option value="gif">GIF</option>
                  <option value="webm">WebM</option>
                </select>
              </label>
              <label className="anim__ctl">
                <span>Resolution</span>
                <select value={resolution} onChange={(e) => setResolution(e.target.value)}>
                  {RES_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </label>
              <label className="anim__ctl">
                <span>Style (default)</span>
                <select
                  value={defaultType}
                  onChange={(e) => setDefaultType(e.target.value as AnimType)}
                >
                  {ANIM_TYPES.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </label>
              {!presentationMode && (
                <button
                  type="button"
                  className="anim__reset"
                  onClick={onResetSequential}
                  disabled={busy}
                >
                  Reset to sequential
                </button>
              )}
              {frames.length >= 2 && (
                <label className="anim__ctl anim__ctl--present">
                  <span>Presentation</span>
                  <input
                    type="checkbox"
                    checked={presentationMode}
                    onChange={(e) => setPresentationMode(e.target.checked)}
                    title="Render the frames as a Magic-Move slide deck (morph + per-element animation)"
                  />
                </label>
              )}
              {presentationMode && (
                <>
                  <label className="anim__ctl">
                    <span>Motion</span>
                    <select
                      value={presentMode}
                      onChange={(e) => setPresentMode(e.target.value as "morph" | "pan")}
                      title="Magic-Move morphs elements in place; Camera pan travels frame-to-frame so connector arrows show"
                    >
                      <option value="morph">Magic-Move (morph)</option>
                      <option value="pan">Camera pan (shows arrows)</option>
                    </select>
                  </label>
                  <label className="anim__ctl">
                    <span>Hold (ms)</span>
                    <input
                      type="number"
                      min={300}
                      step={100}
                      value={holdMs}
                      onChange={(e) => setHoldMs(Math.max(300, +e.target.value || 2200))}
                    />
                  </label>
                  <label className="anim__ctl">
                    <span>Morph (ms)</span>
                    <input
                      type="number"
                      min={0}
                      step={50}
                      value={morphMs}
                      onChange={(e) => setMorphMs(Math.max(0, +e.target.value || 850))}
                    />
                  </label>
                </>
              )}
            </div>

            {presentationMode ? (
              <div className="anim__hint">
                {presentMode === "pan" ? (
                  <>
                    Camera-pan deck: the view travels across your{" "}
                    {slideOrder.length} frames, holding on each. Inter-frame
                    connector arrows (drawn between frames) are visible during
                    the pan. Hold = time per slide, Morph = pan duration.
                  </>
                ) : (
                  <>
                    Magic-Move deck: your {slideOrder.length} frames play as
                    slides, morphing between them. Set each element's entrance
                    and motion below; give two elements the same <code>morphId</code>{" "}
                    to make them glide between slides. Hold = time per slide,
                    Morph = transition length.
                  </>
                )}
              </div>
            ) : (
              <div className="anim__hint">
                Set when each element animates (start), how long (duration, ms), and
                its entrance style. Per-row style "Default" follows the Style dropdown
                above. Order follows start times — gaps and overlaps welcome.
              </div>
            )}

            {presentationMode && presentMode === "morph" && (
              <ul className="anim__timeline anim__present-list">
                {presentEls.length === 0 && (
                  <li className="anim__empty">No elements inside slide frames.</li>
                )}
                {presentEls.map((e) => {
                  const slideNo = slideOrder.indexOf(e.frameId) + 1;
                  return (
                    <li key={e.id} className="anim__row">
                      <span className="anim__idx" title={`Slide ${slideNo}`}>
                        S{slideNo}
                      </span>
                      <span className="anim__label" title={e.id}>
                        {elLabel(e)}
                      </span>
                      <label className="anim__field anim__field--morph">
                        <span>morphId</span>
                        <input
                          type="text"
                          value={animVal(e, "morphId")}
                          placeholder="(pair id)"
                          onChange={(ev) =>
                            setElCustom(e.id, { morphId: ev.target.value.trim() })
                          }
                        />
                      </label>
                      <label className="anim__field anim__field--style">
                        <span>enter</span>
                        <select
                          value={animVal(e, "enter")}
                          onChange={(ev) => setElCustom(e.id, { enter: ev.target.value })}
                        >
                          {PRESENT_ENTER.map((o) => (
                            <option key={o.value} value={o.value}>
                              {o.label}
                            </option>
                          ))}
                        </select>
                      </label>
                      <label className="anim__field anim__field--style">
                        <span>loop</span>
                        <select
                          value={animVal(e, "loop")}
                          onChange={(ev) => setElCustom(e.id, { loop: ev.target.value })}
                        >
                          {PRESENT_LOOP.map((o) => (
                            <option key={o.value} value={o.value}>
                              {o.label}
                            </option>
                          ))}
                        </select>
                      </label>
                    </li>
                  );
                })}
              </ul>
            )}

            {!presentationMode && (
            <ul className="anim__timeline">
              {shown.length === 0 && (
                <li className="anim__empty">No elements in this scope.</li>
              )}
              {shown.map((e, i) => {
                const w = windows[e.id] ?? { start: i * STEP, duration: DEFAULT_DUR };
                return (
                  <li key={e.id} className="anim__row">
                    <span className="anim__idx">{i + 1}</span>
                    <span className="anim__label" title={e.id}>
                      {elLabel(e)}
                    </span>
                    <label className="anim__field">
                      <span>start</span>
                      <input
                        type="number"
                        min={0}
                        step={50}
                        value={w.start}
                        onChange={(ev) =>
                          setWin(e.id, { start: Math.max(0, +ev.target.value || 0) })
                        }
                      />
                    </label>
                    <label className="anim__field">
                      <span>dur</span>
                      <input
                        type="number"
                        min={50}
                        step={50}
                        value={w.duration}
                        onChange={(ev) =>
                          setWin(e.id, { duration: Math.max(50, +ev.target.value || 50) })
                        }
                      />
                    </label>
                    <label className="anim__field anim__field--style">
                      <span>style</span>
                      <select
                        value={w.type ?? ""}
                        onChange={(ev) =>
                          setWin(e.id, {
                            type: (ev.target.value || undefined) as AnimType | undefined,
                          })
                        }
                      >
                        <option value="">Default</option>
                        {ANIM_TYPES.map((o) => (
                          <option key={o.value} value={o.value}>
                            {o.label}
                          </option>
                        ))}
                      </select>
                    </label>
                  </li>
                );
              })}
            </ul>
            )}

            {/* Renders section — all past and in-progress jobs for this diagram */}
            {jobs.length > 0 && (
              <div className="anim__renders">
                <div className="anim__renders-header">
                  <span>Renders</span>
                  <span className="anim__renders-count">{jobs.length}</span>
                </div>

                {preview && (
                  <div className="anim__player">
                    {preview.format === "gif" ? (
                      <img
                        className="anim__player-media"
                        src={preview.url}
                        alt="rendered animation"
                      />
                    ) : (
                      <video
                        className="anim__player-media"
                        src={preview.url}
                        controls
                        autoPlay
                        loop
                        playsInline
                      />
                    )}
                    <button
                      type="button"
                      className="anim__player-close"
                      onClick={() => setPreview(null)}
                      title="Close player"
                    >
                      ✕
                    </button>
                  </div>
                )}
                <ul className="anim__renders-list">
                  {jobs.map((job) => {
                    const isRunning = job.status === "queued" || job.status === "rendering";
                    const isDone = job.status === "done";
                    const liveStatus =
                      isRunning && busy && status ? status.status : job.status;
                    const resultUrl = isDone
                      ? `${animResultUrl(diagramId, job.format)}&t=${job.finishedAt ?? ""}`
                      : null;

                    return (
                      <li
                        key={job.jobId}
                        className={`anim__render-card anim__render-card--${job.status}`}
                      >
                        <span className="anim__render-badge">
                          {job.format.toUpperCase()}
                        </span>
                        <span className="anim__render-res">{job.resolution}</span>
                        <span className="anim__render-time">{relTime(job.startedAt)}</span>
                        <span
                          className={`anim__render-status anim__render-status--${liveStatus}`}
                        >
                          {liveStatus}
                        </span>

                        {isDone && resultUrl && (
                          <>
                            <button
                              type="button"
                              className="anim__render-action"
                              onClick={() =>
                                setPreview({ format: job.format, url: resultUrl })
                              }
                            >
                              {job.format === "gif" ? "View" : "Play"}
                            </button>
                            <a
                              className="anim__render-action"
                              href={resultUrl}
                              download={`${diagramId}.${job.format}`}
                            >
                              Download
                            </a>
                          </>
                        )}

                        {job.spec && (
                          <button
                            type="button"
                            className="anim__render-rerender"
                            disabled={busy}
                            onClick={() => onRerender(job)}
                            title="Load this render's settings into the editor"
                          >
                            Re-render
                          </button>
                        )}

                        {job.status === "error" && (
                          <span className="anim__render-err" title={job.error}>
                            {(job.error ?? "error").slice(0, 40)}
                          </span>
                        )}

                        {!isRunning && (
                          <button
                            type="button"
                            className="anim__render-delete"
                            disabled={deletingId === job.jobId}
                            onClick={() => onDeleteRender(job)}
                            title="Delete this render from the server"
                          >
                            {deletingId === job.jobId ? "…" : "Delete"}
                          </button>
                        )}
                      </li>
                    );
                  })}
                </ul>
              </div>
            )}

            {error && <div className="anim__error">Error: {error}</div>}

            <div className="anim__actions">
              <button
                type="button"
                className="anim__render"
                disabled={
                  busy ||
                  (presentationMode ? slideOrder.length < 2 : shown.length === 0)
                }
                onClick={onRender}
              >
                {busy
                  ? status?.status === "rendering"
                    ? "Rendering…"
                    : "Queued…"
                  : presentationMode
                    ? "Render presentation"
                    : "Render video"}
              </button>
            </div>
          </>
        )}
      </div>
    </Dialog>
  );
};
